/**
 * Workspace do problema: identidade já veio no cabeçalho.
 * Aqui ficam progresso, uma próxima ação e o trabalho da etapa.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Issue, IssueStatus, RootCauseAnalysis } from '../../types/issue';
import { ISSUE_MODE_LABELS, ISSUE_STATUS_LABELS, ISSUE_TYPE_LABELS, getStatusTransitions, isTransitionAllowed } from '../../types/issue';
import type { IssueNextAction } from '../../utils/issueNextAction';
import { workspaceStages } from '../../utils/issueProgress';
import { getIssueOrigin, presentIssueOrigin } from '../../utils/issueOrigin';
import { resolveSelectedStage, stageRelation } from '../../utils/problemStageNav';
import { presentStageLabel } from '../../utils/structuredStageLabel';
import { isCauseConfirmed } from '../../utils/problemCause';
import { efficacyAttempts, evaluateIssueClosure } from '../../utils/efficacyCheck';
import { resolveWorkspaceToolbar } from '../../utils/toolbarCue';
import { standardizationReadiness, type StandardizationKind } from '../../utils/standardization';
import { conclusionAllowsClose } from '../../utils/conclusion';
import type { ActionCountermeasureRecord } from '../../services/actionCountermeasureService';
import { workspaceOpportunityPlacement, workspaceWorkFocus, type WorkspaceTab } from '../../utils/problemWorkspaceView';
import { canCreateOpportunityFromIssue, openOpportunityCount, openOpportunityNotice } from '../../utils/problemOpportunity';
import { showEscalationPrompt } from '../../utils/problemKaizenLink';
import type { ActionStatus, EnterpriseAction } from '../../services/actionService';
import type { IssueMaspEventRecord, StandardizationEventRecord } from '../../services/issueMaspService';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import type { IssueHistoryEntry } from '../../utils/issueHistory';
import type { OrgMember } from '../../hooks/useOrgMembers';
import type { Opportunity } from '../../types/opportunity';
import { ProblemTimeline } from './ProblemTimeline';
import { ProblemStageProgressBar } from './ProblemStageProgressBar';
import { ProblemStagePane, type MaspEditors } from './ProblemStagePane';
import { A3Report } from './A3Report';

interface ProblemCaseWorkspaceProps {
  issue: Issue;
  actions: EnterpriseAction[];
  assigneeNames: Record<string, string>;
  members: OrgMember[];
  nextAction: IssueNextAction;
  projectName: string | null;
  workflowLinked: boolean;
  goalLinked: boolean;
  history: IssueHistoryEntry[];
  onAssign: (userId: string) => void;
  onCreateAction: () => void;
  onSaveAnalysis?: (analysis: RootCauseAnalysis) => void;
  savingAnalysis?: boolean;
  onOpenCause: () => void;
  onOpenActions: () => void;
  onOpenValidation: () => void;
  onOpenAction: (action: EnterpriseAction) => void;
  onOpenProject: () => void;
  onTransition: (status: IssueStatus) => void;
  onInformClosureReason?: () => void;
  opportunities?: Opportunity[];
  canClose?: boolean;
  hasIneffectiveCheck?: boolean;
  onCreateOpportunity?: () => void;
  onOpenOpportunity?: (opportunityId: string) => void;
  efficacyPanel?: React.ReactNode;
  assigneeLabel?: string;
  masp?: MaspEditors;
  actionsState?: 'loading' | 'error';
  eventsState?: 'error';
  executionEventsState?: 'error';
  countermeasures?: ActionCountermeasureRecord[];
  onEditCountermeasure?: (action: EnterpriseAction) => void;
  onCancelCountermeasure?: (action: EnterpriseAction) => void;
  executionEvents?: IssueMaspEventRecord[];
  executionEvidence?: IssueEvidenceRecord[];
  onChangeActionStatus?: (action: EnterpriseAction, status: ActionStatus) => Promise<void>;
  onAddExecutionEvent?: (input: { kind: 'training' | 'deviation'; title: string; body: string; occurredOn: string }) => Promise<void>;
  onAddExecutionEvidence?: (file: File, caption: string) => Promise<void>;
  onDeleteExecutionEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  validationEvidence?: IssueEvidenceRecord[];
  actorId?: string;
  actorRole?: string | null;
  onAddAfterPoint?: (measureId: string, value: number, occurredOn: string) => Promise<void>;
  onConfirmEfficacy?: (note: string) => Promise<void>;
  onRejectEfficacy?: (note: string) => Promise<void>;
  onAddValidationEvidence?: (file: File, caption: string) => Promise<void>;
  onDeleteValidationEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  standardizationEvents?: StandardizationEventRecord[];
  onAddStandardizationEvent?: (input: {
    kind: StandardizationKind;
    title: string;
    body: string;
    audience: string;
    result: string;
    occurredOn: string;
  }) => Promise<void>;
}

export const ProblemCaseWorkspace: React.FC<ProblemCaseWorkspaceProps> = ({
  issue,
  actions,
  assigneeNames,
  members,
  nextAction,
  projectName,
  workflowLinked,
  goalLinked,
  history,
  onAssign,
  onCreateAction,
  onSaveAnalysis,
  savingAnalysis,
  onOpenCause,
  onOpenActions,
  onOpenValidation,
  onOpenAction,
  onOpenProject,
  onTransition,
  onInformClosureReason,
  opportunities = [],
  canClose = false,
  hasIneffectiveCheck = false,
  onCreateOpportunity,
  onOpenOpportunity,
  efficacyPanel,
  assigneeLabel,
  masp,
  countermeasures = [],
  onEditCountermeasure,
  onCancelCountermeasure,
  executionEvents = [],
  executionEvidence = [],
  onChangeActionStatus,
  onAddExecutionEvent,
  onAddExecutionEvidence,
  onDeleteExecutionEvidence,
  validationEvidence = [],
  actorId,
  actorRole,
  onAddAfterPoint,
  onConfirmEfficacy,
  onRejectEfficacy,
  onAddValidationEvidence,
  onDeleteValidationEvidence,
  standardizationEvents = [],
  onAddStandardizationEvent,
  actionsState,
  eventsState,
  executionEventsState,
}) => {
  const navigate = useNavigate();
  const moreRef = useRef<HTMLDivElement>(null);
  const [assigneeId, setAssigneeId] = useState(members[0]?.id ?? '');
  const [moreOpen, setMoreOpen] = useState(false);
  const [tab, setTab] = useState<WorkspaceTab>('work');
  const [a3Open, setA3Open] = useState(false);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const quick = issue.mode === 'quick_correction';
  const focus = workspaceWorkFocus(issue, nextAction);
  const stages = workspaceStages(issue);
  const activeKey = resolveSelectedStage(selectedStage, issue);
  const relation = activeKey ? stageRelation(issue, activeKey) : 'offpath';
  const activeStage = stages.find((stage) => stage.key === activeKey);
  const activeLabel = activeStage ? presentStageLabel(issue.mode, activeStage.key, activeStage.label) : 'Etapa';
  const origin = getIssueOrigin({
    organizationId: issue.organization_id,
    source: issue.source,
    projectId: issue.project_id,
    metadata: issue.metadata,
  });

  useEffect(() => {
    if (!assigneeId && members[0]) setAssigneeId(members[0].id);
  }, [assigneeId, members]);

  useEffect(() => {
    setTab('work');
    setMoreOpen(false);
    setSelectedStage(null);
    setA3Open(false);
  }, [issue.id]);

  useEffect(() => {
    if (!moreOpen) return;
    const close = (event: MouseEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const directActions = actions.filter((action) => action.problem_id === issue.id);
  const activeCountermeasures = directActions.filter((action) => action.status !== 'cancelled');
  const efficacyClosure = evaluateIssueClosure({
    actions: directActions,
    attempts: efficacyAttempts(issue.metadata),
    closureReason: issue.metadata?.closure_reason,
    metadata: issue.metadata,
  });
  const efficacyAllowsStandardization = efficacyClosure.case === 'B' && efficacyClosure.canClose;
  const standardizationAllowsConclusion = conclusionAllowsClose({
    status: issue.status,
    closure: efficacyClosure,
    standardizationReady: standardizationReadiness(standardizationEvents).ready,
    transitionAllowed: isTransitionAllowed(issue.mode, issue.status, 'closed'),
  });
  const otherSteps = (getStatusTransitions(issue.mode)[issue.status] ?? [])
    .filter((status) => status !== nextAction.targetStatus && status !== 'closed')
    .filter((status) => quick || status !== 'monitoring' || efficacyAllowsStandardization);
  const canCreateOpportunity = Boolean(onCreateOpportunity) && canCreateOpportunityFromIssue(issue.status);
  const notice = openOpportunityNotice(openOpportunityCount(opportunities));
  const escalate = showEscalationPrompt({ status: issue.status, hasIneffectiveCheck, canClose });
  workspaceOpportunityPlacement();

  const toolbar = resolveWorkspaceToolbar({
    quick,
    mode: issue.mode,
    status: issue.status,
    causeConfirmed: isCauseConfirmed(issue),
    nextAction,
    activeCountermeasures: activeCountermeasures.length,
    completedCountermeasures: activeCountermeasures.filter((action) => action.status === 'completed').length,
    efficacyAllowsStandardization,
    standardizationAllowsConclusion,
  });
  const actionLabel = !quick && nextAction.key === 'continue_investigation' ? 'Descrever fenômeno' : nextAction.label;
  const toolbarLabel = toolbar.kind === 'transition' || toolbar.kind === 'closure'
    ? toolbar.label
    : toolbar.kind === 'assign'
      ? 'Atribuir'
      : actionLabel;

  const runNext = () => {
    if (toolbar.kind === 'transition') {
      onTransition(toolbar.targetStatus);
      return;
    }
    if (toolbar.kind === 'closure') {
      onInformClosureReason?.();
      return;
    }
    if (nextAction.kind === 'assign') {
      if (assigneeId) onAssign(assigneeId);
      return;
    }
    if (nextAction.key === 'verify_efficacy' || nextAction.kind === 'validate') {
      setTab('work');
      document.getElementById('stage-work')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    if (nextAction.key === 'closure_reason') {
      onInformClosureReason?.();
      return;
    }
    if (nextAction.kind === 'root_cause') {
      chooseStage(issue.status === 'root_cause' ? 'root' : 'analysis');
      return;
    }
    else if (nextAction.kind === 'create_action') onCreateAction();
    else if (nextAction.kind === 'advance_status' && nextAction.targetStatus) onTransition(nextAction.targetStatus);
    else if (nextAction.kind === 'open_workspace') onOpenActions();
  };

  const chooseStage = (key: string) => {
    setSelectedStage(key);
    setTab('work');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-slate-950">
      <ProblemStageProgressBar
        issue={issue}
        actions={actions}
        selectedKey={activeKey}
        onSelect={chooseStage}
      />

      <div className="flex h-12 items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800">
        <div className="flex h-full items-stretch gap-4">
          {([
            ['work', 'Trabalho'],
            ['context', 'Contexto'],
            ['history', 'Histórico'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`border-b-2 text-[13px] ${tab === id ? 'border-slate-900 font-semibold text-slate-950 dark:border-slate-100 dark:text-slate-50' : 'border-transparent font-medium text-slate-500 hover:text-slate-800'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div ref={moreRef} className="relative flex shrink-0 items-center gap-2">
            {toolbar.kind === 'assign' || (toolbar.kind === 'legacy' && nextAction.kind === 'assign') ? (
              <select
                value={assigneeId}
                onChange={(event) => setAssigneeId(event.target.value)}
                className="h-8 max-w-[180px] rounded border border-slate-300 bg-white px-2 text-[12px] dark:border-slate-600 dark:bg-slate-900"
              >
                {members.map((member) => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            ) : null}
            {toolbar.kind !== 'hidden' && toolbar.kind !== 'legacy' || (toolbar.kind === 'legacy' && nextAction.kind !== 'close') ? (
              <button
                type="button"
                disabled={toolbar.kind === 'legacy' && (nextAction.disabled || (nextAction.kind === 'assign' && !assigneeId))}
                onClick={runNext}
                className="inline-flex h-8 items-center whitespace-nowrap rounded bg-blue-600 px-2.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span>{toolbarLabel}</span>
                <span aria-hidden="true">&nbsp;→</span>
              </button>
            ) : null}
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
              className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Mais ações
              <ChevronDown size={14} />
            </button>
            {moreOpen ? (
              <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-60 rounded border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                {otherSteps.map((status) => (
                  <button
                    key={status}
                    type="button"
                    role="menuitem"
                    onClick={() => { setMoreOpen(false); onTransition(status); }}
                    className="block w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    {issue.status === 'closed' && status === 'open' ? 'Reabrir' : ISSUE_STATUS_LABELS[status]}
                  </button>
                ))}
                {canCreateOpportunity ? (
                  <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); onCreateOpportunity?.(); }} className="block w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 dark:text-slate-200">
                    Criar oportunidade de melhoria
                  </button>
                ) : null}
                {issue.status !== 'closed' && nextAction.kind !== 'create_action' && !(!quick && !isCauseConfirmed(issue)) && !(!quick && activeKey === 'plan') ? (
                  <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); onCreateAction(); }} className="block w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 dark:text-slate-200">
                    Nova contramedida
                  </button>
                ) : null}
                {!quick ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setMoreOpen(false); setA3Open(true); setTab('work'); }}
                    className="block w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 dark:text-slate-200"
                  >
                    Ver A3
                  </button>
                ) : null}
                {focus === 'efficacy' || activeKey === 'valid' ? (
                  <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); onOpenValidation(); }} className="block w-full px-3 py-1.5 text-left text-[12px] text-slate-600 hover:bg-slate-50">
                    Configurar critério
                  </button>
                ) : null}
              </div>
            ) : null}
        </div>
      </div>
      {notice || escalate || (nextAction.disabled && nextAction.reason && nextAction.reason !== nextAction.description) ? (
        <p className="py-1 text-[12px] leading-snug text-slate-600 dark:text-slate-300">
          {nextAction.disabled && nextAction.reason && nextAction.reason !== nextAction.description ? nextAction.reason : null}
          {escalate ? <span className="text-amber-800 dark:text-amber-300">A contramedida não resolveu o problema.</span> : null}
          {notice ? <span className="ml-2">{notice}</span> : null}
          {opportunities.map((opportunity) => (
            <button
              key={opportunity.id}
              type="button"
              onClick={() => onOpenOpportunity?.(opportunity.id)}
              className="ml-2 font-medium text-slate-800 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500 dark:text-slate-100"
            >
              {opportunity.title}
            </button>
          ))}
        </p>
      ) : null}

      {tab === 'work' && relation === 'past' && !a3Open ? (
        <div className="flex items-center justify-between gap-3 py-1 text-[12px] text-slate-500">
          <span>Consultando etapa anterior</span>
          <button type="button" onClick={() => setSelectedStage(null)} className="font-medium text-slate-700 hover:underline dark:text-slate-200">
            Voltar à etapa atual
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        {tab === 'work' && a3Open ? (
          <A3Report
            issue={issue}
            actions={actions}
            measures={masp?.measures ?? []}
            strata={masp?.strata ?? []}
            gemba={masp?.gemba ?? []}
            countermeasures={countermeasures}
            events={standardizationEvents}
            executionEvents={executionEvents}
            evidence={masp?.evidence ?? []}
            assigneeNames={assigneeNames}
            onClose={() => setA3Open(false)}
          />
        ) : null}
        {tab === 'work' && !a3Open ? (
          activeKey ? (
            <ProblemStagePane
              issue={issue}
              actions={actions}
              assigneeNames={assigneeNames}
              stageKey={activeKey}
              stageLabel={activeLabel}
              quick={quick}
              efficacyPanel={efficacyPanel}
              onOpenCause={onOpenCause}
              onOpenActions={onOpenActions}
              onOpenAction={onOpenAction}
              onCreateAction={onCreateAction}
              onEditCountermeasure={onEditCountermeasure}
              onCancelCountermeasure={onCancelCountermeasure}
              countermeasures={countermeasures}
              executionEvents={executionEvents}
              executionEvidence={executionEvidence}
              onChangeActionStatus={onChangeActionStatus}
              onAddExecutionEvent={onAddExecutionEvent}
              onAddExecutionEvidence={onAddExecutionEvidence}
              onDeleteExecutionEvidence={onDeleteExecutionEvidence}
              validationEvidence={validationEvidence}
              actorId={actorId}
              actorRole={actorRole}
              onAddAfterPoint={onAddAfterPoint}
              onConfirmEfficacy={onConfirmEfficacy}
              onRejectEfficacy={onRejectEfficacy}
              onAddValidationEvidence={onAddValidationEvidence}
              onDeleteValidationEvidence={onDeleteValidationEvidence}
              standardizationEvents={standardizationEvents}
              onAddStandardizationEvent={onAddStandardizationEvent}
              onSelectStage={chooseStage}
              onSaveAnalysis={onSaveAnalysis}
              savingAnalysis={savingAnalysis}
              masp={masp}
              actionsState={actionsState}
              eventsState={eventsState}
              executionEventsState={executionEventsState}
            />
          ) : (
            <p className="text-[13px] text-slate-600 dark:text-slate-300">{nextAction.description}</p>
          )
        ) : null}

        {tab === 'context' ? (
          <dl className="grid w-fit max-w-full grid-cols-[repeat(auto-fill,minmax(14rem,18rem))] gap-x-6 gap-y-2 text-[13px]">
            <ContextItem label="Origem" value={presentIssueOrigin(origin)} />
            {origin.context.map((line) => (
              <ContextItem key={line.label} label={line.label} value={line.value} />
            ))}
            {origin.navigation ? (
              <div>
                <button type="button" onClick={() => navigate(origin.navigation!.href)} className="text-[13px] font-medium text-blue-700 hover:underline">
                  Abrir origem
                </button>
              </div>
            ) : null}
            {origin.unavailableReason ? <ContextItem label="Navegação" value={origin.unavailableReason} /> : null}
            <ContextItem label="Projeto" value={projectName ?? 'Sem projeto'} action={projectName ? onOpenProject : undefined} />
            {workflowLinked ? <ContextItem label="Workflow" value="Vinculado" /> : null}
            {goalLinked ? <ContextItem label="Meta" value="Vinculada" /> : null}
            {issue.process ? <ContextItem label="Processo" value={issue.process} /> : null}
            {issue.step ? <ContextItem label="Etapa operacional" value={issue.step} /> : null}
            <ContextItem label="Tipo" value={ISSUE_TYPE_LABELS[issue.type]} />
            <ContextItem label="Modo" value={issue.mode ? ISSUE_MODE_LABELS[issue.mode] : '—'} />
            <ContextItem label="Responsável" value={assigneeLabel ?? 'Sem responsável'} />
            <ContextItem label="Aberto em" value={new Date(issue.created_at).toLocaleDateString('pt-BR')} />
          </dl>
        ) : null}

        {tab === 'history' ? (
          <div className="max-w-[960px]">
            {history.length === 0 ? (
              <p className="text-[13px] text-slate-500">Nenhum evento registrado.</p>
            ) : (
              <ProblemTimeline entries={history} />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

function ContextItem({ label, value, action }: { label: string; value: string; action?: () => void }) {
  return (
    <div>
      <dt className="text-[12px] text-slate-500">{label}</dt>
      {action ? (
        <button type="button" onClick={action} className="text-left font-medium text-blue-700 hover:underline">{value}</button>
      ) : (
        <dd className="text-slate-900 dark:text-slate-100">{value}</dd>
      )}
    </div>
  );
}

export default ProblemCaseWorkspace;
